/**
 * Onboarding Wizard
 * 
 * Simple flow:
 * 1. Choose subscription type (individual or organization)
 * 2. For organizations: Enter org info and create org
 * 3. Select plan
 * 4. Enter billing info
 * 5. Create subscription and update user profile
 */

import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {createSubscription, PlanType} from '../../services/SubscriptionService';
import {useAuth} from '../../contexts/AuthContext';
import {createOrganizationAdmin} from '../../services/WebCloudFunctionsService';
import {getDatabase, ref, update} from 'firebase/database';
import {databasePaths} from '../../config/firebase';
import {User, Building, ArrowLeft, ArrowRight, CheckCircle} from 'lucide-react';

const OnboardingWizard: React.FC<{
  organizationId: string;
  organizationName?: string;
  onComplete: () => void;
}> = ({organizationId: initialOrganizationId, organizationName, onComplete}) => {
  const navigate = useNavigate();
  const {user} = useAuth();
  
  // Step 0: Choose subscription type
  const [subscriptionType, setSubscriptionType] = useState<'individual' | 'organization' | null>(null);
  const [finalOrganizationId, setFinalOrganizationId] = useState<string>('');
  
  // Step 1: Organization info (only for organizations)
  const [orgName, setOrgName] = useState(organizationName || user?.displayName || '');
  const [orgDomain, setOrgDomain] = useState('');
  
  // Step 2: Plan selection
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  
  // Step 3: Billing
  const [billingName, setBillingName] = useState(user?.displayName || '');
  const [billingEmail, setBillingEmail] = useState(user?.email || '');
  const [billingPhone, setBillingPhone] = useState('');
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscriptionTypeSelect = (type: 'individual' | 'organization') => {
    setSubscriptionType(type);
    if (type === 'individual') {
      // For individual: organizationId = userId
      if (user?.id) {
        setFinalOrganizationId(user.id);
        setCurrentStep(1); // Skip to plan selection
      }
    } else {
      // For organization: will be set when org is created
      setFinalOrganizationId('');
      setCurrentStep(1); // Go to organization info
    }
  };

  const handleNext = async () => {
    setError(null);
    
    // Step 1: Create organization if needed (organizations only)
    if (subscriptionType === 'organization' && currentStep === 1 && !finalOrganizationId) {
      if (!orgName.trim()) {
        setError('Organization name is required');
        return;
      }
      
      setIsLoading(true);
      try {
        const orgData = {
          name: orgName,
          domain: orgDomain || '',
          description: '',
          settings: {
            allowPublicJoin: false,
            requireApproval: true,
            customChallenges: true,
            leaderboardVisible: true,
            teamCreationOpen: true,
          },
        };
        const createdOrg = await createOrganizationAdmin(orgData);
        const newOrgId = (createdOrg as any)?.id || (createdOrg as any)?.organizationId || (createdOrg as any)?.data?.id;
        if (newOrgId) {
          setFinalOrganizationId(newOrgId);
          setCurrentStep(2);
        } else {
          setError('Failed to create organization');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to create organization');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Step 2: Plan selection - move to billing
    if (currentStep === 1 && subscriptionType === 'individual') {
      if (!selectedPlan) {
        setError('Please select a plan');
        return;
      }
      setCurrentStep(2);
      return;
    }
    
    if (currentStep === 2 && subscriptionType === 'organization') {
      if (!selectedPlan) {
        setError('Please select a plan');
        return;
      }
      setCurrentStep(3);
      return;
    }
    
    // Final step: Create subscription and update user profile
    if ((currentStep === 2 && subscriptionType === 'individual') || 
        (currentStep === 3 && subscriptionType === 'organization')) {
      if (!selectedPlan || !finalOrganizationId) {
        setError('Missing required information');
        return;
      }
      if (!billingName.trim() || !billingEmail.trim()) {
        setError('Billing name and email are required');
        return;
      }
      
      setIsLoading(true);
      try {
        // Create subscription
        await createSubscription(finalOrganizationId, selectedPlan);
        
        // Update user profile in Firebase with organizationId
        if (user?.id) {
          const database = getDatabase();
          const userRef = ref(database, `${databasePaths.users()}/${user.id}`);
          
          const updates: any = {
            organizationId: finalOrganizationId
          };
          
          if (subscriptionType === 'individual') {
            updates.organizationName = user.displayName || 'Personal Account';
          } else {
            updates.organizationName = orgName;
          }
          
          await update(userRef, updates);
        }
        
        onComplete();
        navigate(`/subscription/success?org=${finalOrganizationId}`);
      } catch (err: any) {
        setError(err.message || 'Failed to create subscription');
        setIsLoading(false);
      }
      return;
    }
  };

  const handleBack = () => {
    if (currentStep === 1 && subscriptionType !== null) {
      setSubscriptionType(null);
      setCurrentStep(0);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 md:p-10 border border-slate-200/50 dark:border-slate-800/50">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none"></div>
          <div className="relative z-10">
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {/* Step 0: Subscription Type */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-2">
                    Choose Your Subscription Type
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-400">
                    Select whether you want an individual or organization subscription
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button
                    onClick={() => handleSubscriptionTypeSelect('individual')}
                    className="group relative p-8 border-2 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/25 text-left bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500"
                  >
                    <div className="flex flex-col items-start">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <User className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Individual</h3>
                      <p className="text-slate-600 dark:text-slate-400 mb-4">
                        Perfect for personal use or solo users
                      </p>
                      <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                        <li>• Personal account</li>
                        <li>• Individual plan options</li>
                        <li>• Simple setup</li>
                      </ul>
                    </div>
                  </button>
                  <button
                    onClick={() => handleSubscriptionTypeSelect('organization')}
                    className="group relative p-8 border-2 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/25 text-left bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500"
                  >
                    <div className="flex flex-col items-start">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Building className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Organization</h3>
                      <p className="text-slate-600 dark:text-slate-400 mb-4">
                        For teams, companies, or groups
                      </p>
                      <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                        <li>• Multiple team members</li>
                        <li>• Organization-wide features</li>
                        <li>• Team management tools</li>
                      </ul>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Organization Info (organizations only) */}
            {currentStep === 1 && subscriptionType === 'organization' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-2">
                    Organization Information
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-400">
                    Let's set up your organization profile
                  </p>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={e => setOrgName(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-slate-800 dark:text-white transition-all"
                      placeholder="Acme Corporation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Domain (optional)
                    </label>
                    <input
                      type="text"
                      value={orgDomain}
                      onChange={e => setOrgDomain(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-slate-800 dark:text-white transition-all"
                      placeholder="acme.com"
                    />
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                      Used for automatic user organization assignment
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1 (individual) or Step 2 (organization): Plan Selection */}
            {((currentStep === 1 && subscriptionType === 'individual') || 
              (currentStep === 2 && subscriptionType === 'organization')) && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-2">
                    Choose Your Plan
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-400">
                    Start with a 7-day free trial. No credit card required.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscriptionType === 'individual' ? (
                    <button
                      onClick={() => setSelectedPlan('individual')}
                      className={`p-6 border-2 rounded-xl transition-all text-left ${
                        selectedPlan === 'individual' 
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 bg-white dark:bg-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Individual</h3>
                        <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">$5/month</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">Perfect for personal use</p>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setSelectedPlan('starter')}
                        className={`p-6 border-2 rounded-xl transition-all text-left ${
                          selectedPlan === 'starter' 
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg' 
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 bg-white dark:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">Starter</h3>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">$5/user</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">For small teams</p>
                      </button>
                      <button
                        onClick={() => setSelectedPlan('professional')}
                        className={`p-6 border-2 rounded-xl transition-all text-left ${
                          selectedPlan === 'professional' 
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg' 
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 bg-white dark:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">Professional</h3>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">$8/user</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">For growing teams</p>
                      </button>
                      <button
                        onClick={() => setSelectedPlan('enterprise')}
                        className={`p-6 border-2 rounded-xl transition-all text-left ${
                          selectedPlan === 'enterprise' 
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg' 
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 bg-white dark:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">Enterprise</h3>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">Custom</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">For large organizations</p>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Step 2 (individual) or Step 3 (organization): Billing */}
            {((currentStep === 2 && subscriptionType === 'individual') || 
              (currentStep === 3 && subscriptionType === 'organization')) && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-2">
                    Billing Contact
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-400">
                    Who should we contact for billing questions?
                  </p>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={billingName}
                      onChange={e => setBillingName(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-slate-800 dark:text-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={billingEmail}
                      onChange={e => setBillingEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-slate-800 dark:text-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={billingPhone}
                      onChange={e => setBillingPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-slate-800 dark:text-white transition-all"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-colors flex items-center space-x-2 ${
                  currentStep === 0
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              {currentStep === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Select a subscription type to continue
                </div>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-[1.02] flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isLoading ? (
                    'Processing...'
                  ) : (currentStep === 2 && subscriptionType === 'individual') || 
                      (currentStep === 3 && subscriptionType === 'organization') ? (
                    <>
                      <span>Complete Setup</span>
                      <CheckCircle className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span>Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
